import { z } from 'zod';
import {
  AccountCreateRequest,
  AccountSummary,
  AllocationCopyForwardRequest,
  AllocationMatrixResponse,
  AllocationUpsertRequest,
  ArAgingResponse,
  AttendanceLockResponse,
  AttendanceMonthResponse,
  AttendanceUnlockRequest,
  BankTemplateCreateRequest,
  ChecklistResponse,
  ComponentTemplateCreateRequest,
  ComponentTemplateSummary,
  HolidayCreateRequest,
  HolidaySummary,
  LeaveBalance,
  LeaveRequestCancelRequest,
  LeaveRequestCreateRequest,
  LeaveRequestDecideRequest,
  LeaveRequestSummary,
  LeaveStatus,
  LeaveTypeCreateRequest,
  LeaveTypeSummary,
  LeaveTypeUpdateRequest,
  PayrollRunCreateRequest,
  PayrollRunDetail,
  PayrollRunSummary,
  PayslipListEntry,
  CacResponse,
  LtvResponse,
  ProjectCashResponse,
  TcoResponse,
  PersonRateCreateRequest,
  PersonRateSummary,
  PeriodSummary,
  ReopenRequest,
  SalaryStructureCreateRequest,
  SalaryStructureSummary,
  SnapshotSummary,
  StatutoryConfigCreateRequest,
  StatutoryConfigSummary,
  StatutoryConfigVerifyRequest,
  CommentCreateRequest,
  CommentSummary,
  IdeaApproveRequest,
  IdeaApproveResult,
  IdeaCreateRequest,
  IdeaDetail,
  IdeaParkRequest,
  IdeaPickUpRequest,
  IdeaProposeRequest,
  IdeaRejectRequest,
  IdeaResurfaceRequest,
  IdeaStatus,
  IdeaSummary,
  BudgetVarianceResponse,
  DataHealthResponse,
  DsoResponse,
  MetricEntry,
  NotificationPreferences,
  NotificationPreferencesUpdateRequest,
  PayrollCostRatioResponse,
  PeopleKpiResponse,
  ProjectPnlResponse,
  QueueItemsResponse,
  ReconciliationLagResponse,
  UtilizationResponse,
  VoteToggleResult,
  BankTemplateSummary,
  BudgetRevisionCreateRequest,
  CategoryCreateRequest,
  CategoryKind,
  CategorySummary,
  ChannelSpendGridCell,
  ChannelSpendUpsertRequest,
  ClientCreateRequest,
  ClientCreateResponse,
  ClientDetail,
  ClientMergeRequest,
  ClientTimelineEntry,
  ClientUpdateRequest,
  ClientsSimpleResponse,
  GstinLookupResult,
  GstinLookupStatus,
  ClientSummary,
  CompanyProfileCreateRequest,
  CompanyProfileLogoRequest,
  CompanyProfileSummary,
  ContactCreateRequest,
  CostCenterCreateRequest,
  CostCenterRenameRequest,
  CostCenterSummary,
  CreditNoteCreateRequest,
  DocType,
  DocumentCreateRequest,
  DocumentDetail,
  DocumentListResponse,
  DocumentStatus,
  DocumentUpdateDraftRequest,
  DocumentNumberFormatCreateRequest,
  DocumentNumberFormatListResponse,
  NextDocumentNumberResponse,
  LeadChannelSummary,
  AllocationProposalConfirmResponse,
  AllocationProposalResponse,
  ClockInRequest,
  ClockOutRequest,
  CurrentWorkSessionResponse,
  LeadChannelUpdateRequest,
  LeadChannelUpsertRequest,
  ChangePasswordRequest,
  ForgotPasswordRequest,
  LoginRequest,
  LoginResponse,
  MeResponse,
  NextEmployeeIdResponse,
  MyTasksInbox,
  NotificationSummary,
  PastedRowsPreviewRequest,
  PastedRowsPreviewResponse,
  PaymentApplicationRequest,
  PersonCreateRequest,
  PersonDetail,
  PersonExitRequest,
  PersonListResponse,
  PersonResetPasswordRequest,
  PersonSetRolesRequest,
  PersonSimple,
  PersonUpdateRequest,
  ResetPasswordRequest,
  ProjectCreateRequest,
  ProjectDetail,
  RenewalInput,
  RenewalKind,
  RenewalListResponse,
  RenewalStatusFilter,
  RenewalUpdateRequest,
  ProjectStatus,
  ProjectStatusChangeRequest,
  ProjectUpdateRequest,
  ProjectSummary,
  ProjectType,
  ShareBundleRequest,
  StatementBulkConfirmRequest,
  StatementBulkSkipRequest,
  StatementFileStatus,
  StatementPostRequest,
  StatementPostResult,
  StatementRowConfirmRequest,
  StatementRowSkipRequest,
  StatementRowSummary,
  StatementSummary,
  StatementUploadResult,
  AttendanceCalendarResponse,
  AbsenceBulkRulingRequest,
  AbsenceBulkRulingResult,
  AbsencesSummary,
  AttendanceDayRow,
  BackfillSessionRequest,
  TaskComment,
  TaskCommentCreateRequest,
  TaskCreateRequest,
  TaskHistoryEntry,
  TaskStatusCreateRequest,
  TaskStatusDef,
  TaskStatusUpdateRequest,
  TaskTreeNode,
  TaskUpdateRequest,
  TransactionBatchCreateRequest,
  TransactionCreateRequest,
  TransactionDetail,
  TransactionListResponse,
  TransactionMapRequest,
  TransactionReverseRequest,
  TransactionStatus,
  TransferCreateRequest,
  TwoFactorSetupResponse,
  TwoFactorVerifyRequest,
  VaultDocSummary,
  VaultDocTypeCreateRequest,
  VaultDocTypeSummary,
  WriteOffRequest,
  type VaultDocCreateMetadata,
  ApiTokenCreateRequest,
  ApiTokenCreateResponse,
  ApiTokenSummary,
  ProspectCreateRequest,
  ProspectDetail,
  ProspectListFilters,
  ProspectStage,
  ProspectStageCorrectionRequest,
  WorkSessionAdjustRequest,
  WorkSessionDetail,
  ProspectStageTransitionRequest,
  ProspectSummary,
  ProspectTransitionResponse,
  ProspectUpdateRequest,
  SalesActivityCreateRequest,
  SalesActivitySummary,
  SalesDashboardResponse,
  SalesDashboardWindow,
  ServiceOfferingSummary,
  ServiceOfferingUpsertRequest,
  CsuiteMemberSummary,
  CsuiteMemberUpsertRequest,
} from '@bits/shared/zod';
import { EmployeeIdFormatListResponse, type EmployeeIdFormatCreateRequest } from '@bits/shared/zod';
import { MyProfile, PersonAvatarsResponse, type MyProfileUpdateRequest } from '@bits/shared/zod';
import {
  ChatCall,
  ChatConversationListResponse,
  ChatConversationSummary,
  ChatDirectConversationResponse,
  ChatDirectoryResponse,
  ChatIceServersResponse,
  ChatSfuPublishResponse,
  ChatSfuSessionResponse,
  ChatSfuSubscribeResponse,
  ChatMessage,
  ChatMessagesResponse,
  ChatSearchResponse,
  type ChatCallSignalRequest,
  type ChatCallStartRequest,
  type ChatSfuPublishRequest,
  type ChatSfuRenegotiateRequest,
  type ChatSfuSubscribeRequest,
  type ChatSfuUnpublishRequest,
  type ChatSfuUnsubscribeRequest,
  type ChatGroupCreateRequest,
  type ChatGroupUpdateRequest,
  type ChatMembersAddRequest,
  type ChatAttachmentUploadMetadata,
  type ChatMessageCreateRequest,
  type ChatMessageEditRequest,
  type ChatReactionEmoji,
  ChatMessageRevisionsResponse,
} from '@bits/shared/zod';

const BASE = (import.meta.env.VITE_API_BASE ?? '') as string;

export class ApiRequestError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public details: Record<string, unknown> = {},
  ) {
    super(message);
    this.name = 'ApiRequestError';
  }
}

async function request<TSchema extends z.ZodTypeAny>(
  path: string,
  init: RequestInit,
  schema: TSchema,
): Promise<z.infer<TSchema>> {
  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, {
      ...init,
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        ...(init.body && !(init.body instanceof FormData)
          ? { 'Content-Type': 'application/json' }
          : {}),
        ...(init.headers ?? {}),
      },
    });
  } catch {
    throw new ApiRequestError(0, 'network_error', 'Cannot reach the server');
  }

  if (res.status === 401 && !path.startsWith('/api/auth/')) {
    if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
      window.location.assign('/login');
    }
    throw new ApiRequestError(401, 'unauthenticated', 'Session required');
  }

  const text = await res.text();
  let json: unknown = null;
  if (text) {
    try {
      json = JSON.parse(text);
    } catch {
      // A proxy or crash can answer with HTML where JSON was expected.
      throw new ApiRequestError(res.status, res.ok ? 'bad_response' : 'http_error', '');
    }
  }

  if (!res.ok) {
    const err = json as {
      code?: string;
      message?: string;
      details?: Record<string, unknown>;
    } | null;
    throw new ApiRequestError(
      res.status,
      err?.code ?? 'http_error',
      err?.message ?? '',
      err?.details ?? {},
    );
  }

  try {
    return schema.parse(json);
  } catch {
    throw new ApiRequestError(res.status, 'bad_response', '');
  }
}

function get<TSchema extends z.ZodTypeAny>(
  path: string,
  schema: TSchema,
): Promise<z.infer<TSchema>> {
  return request(path, { method: 'GET' }, schema);
}

function post<TBody, TSchema extends z.ZodTypeAny>(
  path: string,
  body: TBody,
  schema: TSchema,
): Promise<z.infer<TSchema>> {
  return request(path, { method: 'POST', body: JSON.stringify(body) }, schema);
}

function patch<TBody, TSchema extends z.ZodTypeAny>(
  path: string,
  body: TBody,
  schema: TSchema,
): Promise<z.infer<TSchema>> {
  return request(path, { method: 'PATCH', body: JSON.stringify(body) }, schema);
}

function put<TBody, TSchema extends z.ZodTypeAny>(
  path: string,
  body: TBody,
  schema: TSchema,
): Promise<z.infer<TSchema>> {
  return request(path, { method: 'PUT', body: JSON.stringify(body) }, schema);
}

async function postMultipart<TSchema extends z.ZodTypeAny>(
  path: string,
  form: FormData,
  schema: TSchema,
): Promise<z.infer<TSchema>> {
  return request(path, { method: 'POST', body: form }, schema);
}

/**
 * A failed download answers with JSON when the server raised it and HTML when
 * something upstream did, so the body is only used when it parses as our own
 * error envelope. Raw text never reaches the UI.
 */
async function blobError(res: Response): Promise<ApiRequestError> {
  const text = await res.text().catch(() => '');
  try {
    const err = JSON.parse(text) as { code?: string; message?: string };
    return new ApiRequestError(res.status, err.code ?? 'http_error', err.message ?? '');
  } catch {
    return new ApiRequestError(res.status, 'http_error', '');
  }
}

async function fetchBlob(path: string, init: RequestInit, accept: string): Promise<Blob> {
  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, {
      ...init,
      credentials: 'include',
      headers: { Accept: accept, ...(init.headers ?? {}) },
    });
  } catch {
    throw new ApiRequestError(0, 'network_error', 'Cannot reach the server');
  }
  if (!res.ok) throw await blobError(res);
  return res.blob();
}

async function postForBlob(path: string, body: unknown): Promise<Blob> {
  return fetchBlob(
    path,
    { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) },
    'application/zip',
  );
}

const EmptyResponse = z.object({}).passthrough();
const NotificationsResponse = z.array(NotificationSummary);
const CreatePersonResponse = z.object({ personId: z.string(), employeeId: z.string() });

const CreateIdResponse = z.object({ id: z.string() });
const ChatMembersAddedResponse = z.object({ added: z.array(z.string()) });
const CreateClientIdResponse = z.object({ id: z.string() });
const CompanyProfileListResponse = z.array(CompanyProfileSummary);

const VaultDocTypesResponse = z.array(VaultDocTypeSummary);
const VaultDocsResponse = z.array(VaultDocSummary);

const LeadChannelsResponse = z.array(LeadChannelSummary);
const ChannelSpendResponse = z.array(ChannelSpendGridCell);

const ClientsListResponse = z.object({
  items: z.array(ClientSummary),
  total: z.number().int(),
  page: z.number().int(),
  pageSize: z.number().int(),
});

const UpsertChannelResponse = z.object({ id: z.string(), created: z.boolean() });

const CostCentersResponse = z.array(CostCenterSummary);
const ProjectsResponse = z.array(ProjectSummary);
const ProjectCreateResponse = z.object({ projectId: z.string() });
const ToggleActualHoursResponse = z.object({ actualHoursOn: z.boolean() });
const TaskTreeResponse = z.array(TaskTreeNode);
const TaskHistoryResponse = z.array(TaskHistoryEntry);
const TaskCommentsResponse = z.array(TaskComment);
const TaskStatusesResponse = z.array(TaskStatusDef);
const TaskTemplateItem = z.object({ title: z.string() });
const TaskTemplatesResponse = z.array(TaskTemplateItem);
const PeopleSimpleResponse = z.array(PersonSimple);
const CopyForwardResponse = z.object({
  month: z.string().regex(/^\d{4}-\d{2}$/),
  insertedCount: z.number().int(),
});

const AccountsListResponse = z.array(AccountSummary);
const CategoriesListResponse = z.array(CategorySummary);
const CreateTransactionResponse = z.object({ transactionId: z.string() });
const CreateBatchResponse = z.object({
  insertedCount: z.number().int(),
  transactionIds: z.array(z.string()),
});
const CreateTransferResponse = z.object({
  transferGroupId: z.string(),
  transactionIds: z.tuple([z.string(), z.string()]),
});
const CreateReversalResponse = z.object({ reversalTxnId: z.string() });

const BankTemplatesListResponse = z.array(BankTemplateSummary);
const StatementsListResponse = z.array(StatementSummary);
const StatementRowsResponse = z.object({
  items: z.array(StatementRowSummary),
  total: z.number().int(),
});
const ConfirmRowResponse = z.object({ transactionId: z.string() });
const BulkSkipResponse = z.object({ skippedCount: z.number().int() });
const BulkConfirmResponse = z.object({
  confirmedCount: z.number().int(),
  transactionIds: z.array(z.string()),
});
const RerunSuggestResponse = z.object({ matchedCount: z.number().int() });

const ProspectListResponse = z.object({
  items: z.array(ProspectSummary),
  total: z.number().int(),
  page: z.number().int(),
  pageSize: z.number().int(),
});

const ServiceOfferingUpsertResponse = z.object({ id: z.string(), created: z.boolean() });
const CsuiteMemberUpsertResponse = z.object({ ok: z.boolean() });

export const api = {
  me: () => get('/api/auth/me', MeResponse),

  // A rejected sign-in answers 401 with a valid LoginResponse body: the outcome
  // is data the form renders, not a transport failure.
  login: async (body: LoginRequest) => {
    try {
      return await post('/api/auth/login', body, LoginResponse);
    } catch (err) {
      if (err instanceof ApiRequestError && err.status === 401 && err.code === 'http_error') {
        const invalid = LoginResponse.safeParse({ status: 'invalid' });
        if (invalid.success) return invalid.data;
      }
      throw err;
    }
  },

  logout: () => post('/api/auth/logout', {}, EmptyResponse),

  changePassword: (body: ChangePasswordRequest) =>
    post('/api/auth/change-password', body, EmptyResponse),

  forgotPassword: (body: ForgotPasswordRequest) =>
    post('/api/auth/forgot-password', body, EmptyResponse),

  resetPassword: (body: ResetPasswordRequest) =>
    post('/api/auth/reset-password', body, EmptyResponse),

  startTwoFactorSetup: () => post('/api/auth/2fa/setup', {}, TwoFactorSetupResponse),

  verifyTwoFactor: (body: z.infer<typeof TwoFactorVerifyRequest>) =>
    post('/api/auth/2fa/verify', body, EmptyResponse),

  createPerson: (body: PersonCreateRequest) => post('/api/people', body, CreatePersonResponse),

  listPeopleSimple: () => get('/api/people/simple', PeopleSimpleResponse),

  // ----- People & Access (admin) -----
  listPeople: () => get('/api/people', PersonListResponse),
  nextEmployeeId: (dateJoined: string) =>
    get(
      `/api/people/next-employee-id?dateJoined=${encodeURIComponent(dateJoined)}`,
      NextEmployeeIdResponse,
    ),
  listEmployeeIdFormats: () => get('/api/people/employee-id-format', EmployeeIdFormatListResponse),
  addEmployeeIdFormat: (body: EmployeeIdFormatCreateRequest) =>
    post('/api/people/employee-id-format', body, EmptyResponse),
  getPerson: (id: string) => get(`/api/people/${id}`, PersonDetail),
  updatePerson: (id: string, body: PersonUpdateRequest) =>
    patch(`/api/people/${id}`, body, EmptyResponse),
  setPersonRoles: (id: string, body: PersonSetRolesRequest) =>
    post(`/api/people/${id}/roles`, body, EmptyResponse),
  resetPersonPassword: (id: string, body: PersonResetPasswordRequest) =>
    post(`/api/people/${id}/reset-password`, body, EmptyResponse),
  resetPersonTwoFactor: (id: string) => post(`/api/people/${id}/reset-2fa`, {}, EmptyResponse),
  exitPerson: (id: string, body: PersonExitRequest) =>
    post(`/api/people/${id}/exit`, body, EmptyResponse),
  reactivatePerson: (id: string) => post(`/api/people/${id}/reactivate`, {}, EmptyResponse),

  // ----- My profile -----
  myProfile: () => get('/api/me/profile', MyProfile),
  updateMyProfile: (body: MyProfileUpdateRequest) => patch('/api/me/profile', body, MyProfile),
  uploadMyAvatar: (file: File) => {
    const form = new FormData();
    form.append('file', file, file.name);
    return postMultipart('/api/me/avatar', form, MyProfile);
  },
  removeMyAvatar: () => post('/api/me/avatar/remove', {}, MyProfile),
  listAvatars: () => get('/api/people/avatars', PersonAvatarsResponse),
  avatarUrl: (personId: string, version: string) =>
    `/api/people/${personId}/avatar?v=${encodeURIComponent(version)}`,

  listNotifications: () => get('/api/notifications', NotificationsResponse),

  markNotificationRead: (id: string) => post(`/api/notifications/${id}/read`, {}, EmptyResponse),

  // ----- Chat -----
  chatDirectory: (q?: string) =>
    get(`/api/chat/directory${q ? `?q=${encodeURIComponent(q)}` : ''}`, ChatDirectoryResponse),
  chatConversations: () => get('/api/chat/conversations', ChatConversationListResponse),
  chatOpenDirect: (personId: string) =>
    post('/api/chat/conversations/direct', { personId }, ChatDirectConversationResponse),
  chatCreateGroup: (body: ChatGroupCreateRequest) =>
    post('/api/chat/conversations/group', body, CreateIdResponse),
  chatConversation: (id: string) => get(`/api/chat/conversations/${id}`, ChatConversationSummary),
  chatRenameGroup: (id: string, body: ChatGroupUpdateRequest) =>
    patch(`/api/chat/conversations/${id}`, body, EmptyResponse),
  chatArchiveGroup: (id: string) => post(`/api/chat/conversations/${id}/archive`, {}, EmptyResponse),
  chatUnarchiveGroup: (id: string) =>
    post(`/api/chat/conversations/${id}/unarchive`, {}, EmptyResponse),
  chatAddMembers: (id: string, body: ChatMembersAddRequest) =>
    post(`/api/chat/conversations/${id}/members`, body, ChatMembersAddedResponse),
  chatRemoveMember: (id: string, personId: string) =>
    post(`/api/chat/conversations/${id}/members/${personId}/remove`, {}, EmptyResponse),
  chatLeave: (id: string) => post(`/api/chat/conversations/${id}/leave`, {}, EmptyResponse),
  chatMessages: (id: string, before?: string) =>
    get(
      `/api/chat/conversations/${id}/messages${before ? `?before=${before}` : ''}`,
      ChatMessagesResponse,
    ),
  chatSend: (id: string, body: ChatMessageCreateRequest) =>
    post(`/api/chat/conversations/${id}/messages`, body, ChatMessage),
  chatUploadAttachment: (
    id: string,
    metadata: ChatAttachmentUploadMetadata,
    file: Blob,
    fileName: string,
  ) => {
    const form = new FormData();
    form.append('metadata', JSON.stringify(metadata));
    form.append('file', file, fileName);
    return postMultipart(`/api/chat/conversations/${id}/attachments`, form, ChatMessage);
  },
  chatRedact: (id: string, messageId: string) =>
    post(`/api/chat/conversations/${id}/messages/${messageId}/redact`, {}, EmptyResponse),
  chatEditMessage: (id: string, messageId: string, body: ChatMessageEditRequest) =>
    patch(`/api/chat/conversations/${id}/messages/${messageId}`, body, ChatMessage),
  chatRevisions: (id: string, messageId: string) =>
    get(`/api/chat/conversations/${id}/messages/${messageId}/revisions`, ChatMessageRevisionsResponse),
  chatToggleReaction: (id: string, messageId: string, emoji: ChatReactionEmoji) =>
    post(`/api/chat/conversations/${id}/messages/${messageId}/reactions`, { emoji }, ChatMessage),
  chatMarkRead: (id: string, upToMessageId: string) =>
    post(`/api/chat/conversations/${id}/read`, { upToMessageId }, EmptyResponse),
  chatTyping: (id: string) => post(`/api/chat/conversations/${id}/typing`, {}, EmptyResponse),
  chatSearch: (q: string) => get(`/api/chat/search?q=${encodeURIComponent(q)}`, ChatSearchResponse),
  chatStartCall: (body: ChatCallStartRequest) => post('/api/chat/calls', body, ChatCall),
  chatGetCall: (id: string) => get(`/api/chat/calls/${id}`, ChatCall),
  chatAnswerCall: (id: string) => post(`/api/chat/calls/${id}/answer`, {}, ChatCall),
  chatDeclineCall: (id: string) => post(`/api/chat/calls/${id}/decline`, {}, ChatCall),
  chatLeaveCall: (id: string) => post(`/api/chat/calls/${id}/leave`, {}, ChatCall),
  chatSignal: (id: string, body: ChatCallSignalRequest) =>
    post(`/api/chat/calls/${id}/signal`, body, EmptyResponse),
  chatIceServers: () => get('/api/chat/ice-servers', ChatIceServersResponse),
  chatSfuSession: (id: string) => post(`/api/chat/calls/${id}/sfu/session`, {}, ChatSfuSessionResponse),
  chatSfuPublish: (id: string, body: ChatSfuPublishRequest) =>
    post(`/api/chat/calls/${id}/sfu/publish`, body, ChatSfuPublishResponse),
  chatSfuSubscribe: (id: string, body: ChatSfuSubscribeRequest) =>
    post(`/api/chat/calls/${id}/sfu/subscribe`, body, ChatSfuSubscribeResponse),
  chatSfuRenegotiate: (id: string, body: ChatSfuRenegotiateRequest) =>
    put(`/api/chat/calls/${id}/sfu/renegotiate`, body, EmptyResponse).then(() => undefined),
  chatSfuUnpublish: (id: string, body: ChatSfuUnpublishRequest) =>
    put(`/api/chat/calls/${id}/sfu/unpublish`, body, EmptyResponse).then(() => undefined),
  chatSfuUnsubscribe: (id: string, body: ChatSfuUnsubscribeRequest) =>
    put(`/api/chat/calls/${id}/sfu/unsubscribe`, body, EmptyResponse).then(() => undefined),

  // ----- Company Profile -----
  getCurrentCompanyProfile: () => get('/api/company/profile/current', CompanyProfileSummary),
  listCompanyProfiles: () => get('/api/company/profile/all', CompanyProfileListResponse),
  createCompanyProfile: (body: CompanyProfileCreateRequest) =>
    post('/api/company/profile', body, CreateIdResponse),
  uploadCompanyLogo: (file: File) => {
    const form = new FormData();
    form.append('file', file, file.name);
    return postMultipart('/api/company/profile/logo', form, CreateIdResponse);
  },
  companyLogoUrl: (attachmentId: string) => `/api/company/profile/logo/${attachmentId}`,
  setCompanyLogo: (profileId: string, body: CompanyProfileLogoRequest) =>
    post(`/api/company/profile/${profileId}/logo`, body, CreateIdResponse),

  // ----- Vault -----
  listVaultDocTypes: () => get('/api/vault/doc-types', VaultDocTypesResponse),
  createVaultDocType: (body: VaultDocTypeCreateRequest) =>
    post('/api/vault/doc-types', body, CreateIdResponse),
  deactivateVaultDocType: (id: string) =>
    post(`/api/vault/doc-types/${id}/deactivate`, {}, EmptyResponse),
  listVaultDocs: (params: { typeId?: string; expiringWithinDays?: number } = {}) => {
    const qs = new URLSearchParams();
    if (params.typeId) qs.set('typeId', params.typeId);
    if (params.expiringWithinDays !== undefined) {
      qs.set('expiringWithinDays', String(params.expiringWithinDays));
    }
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return get(`/api/vault/docs${suffix}`, VaultDocsResponse);
  },
  uploadVaultDoc: (metadata: VaultDocCreateMetadata, file: File) => {
    const form = new FormData();
    form.append('metadata', JSON.stringify(metadata));
    form.append('file', file, file.name);
    return postMultipart('/api/vault/docs', form, CreateIdResponse);
  },
  supersedeVaultDoc: (id: string, metadata: Partial<VaultDocCreateMetadata>, file: File) => {
    const form = new FormData();
    form.append('metadata', JSON.stringify(metadata));
    form.append('file', file, file.name);
    return postMultipart(`/api/vault/docs/${id}/supersede`, form, CreateIdResponse);
  },
  downloadShareBundle: (body: ShareBundleRequest) => postForBlob('/api/vault/share-bundle', body),
  downloadVaultDoc: (id: string): Promise<Blob> =>
    fetchBlob(`/api/vault/docs/${id}/download`, { method: 'GET' }, '*/*'),

  // ----- CRM channels -----
  listLeadChannels: () => get('/api/crm/channels', LeadChannelsResponse),
  upsertLeadChannel: (body: LeadChannelUpsertRequest) =>
    post('/api/crm/channels', body, UpsertChannelResponse),
  updateLeadChannel: (id: string, body: LeadChannelUpdateRequest) =>
    patch(`/api/crm/channels/${id}`, body, EmptyResponse),
  upsertChannelSpend: (channelId: string, body: ChannelSpendUpsertRequest) =>
    post(`/api/crm/channels/${channelId}/spend`, body, EmptyResponse),
  getChannelSpend: (months: number) =>
    get(`/api/crm/channels/spend?months=${months}`, ChannelSpendResponse),

  // ----- CRM clients -----
  createClient: (body: ClientCreateRequest) => post('/api/crm/clients', body, ClientCreateResponse),
  gstinLookupStatus: () => get('/api/crm/gstin-lookup', GstinLookupStatus),
  lookupGstin: (gstin: string) =>
    get(
      `/api/crm/gstin-lookup/${encodeURIComponent(gstin.trim().toUpperCase())}`,
      GstinLookupResult,
    ),
  listClients: (
    params: {
      status?: 'active' | 'archived' | 'merged';
      search?: string;
      page?: number;
      pageSize?: number;
    } = {},
  ) => {
    const qs = new URLSearchParams();
    if (params.status) qs.set('status', params.status);
    if (params.search) qs.set('search', params.search);
    if (params.page) qs.set('page', String(params.page));
    if (params.pageSize) qs.set('pageSize', String(params.pageSize));
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return get(`/api/crm/clients${suffix}`, ClientsListResponse);
  },
  listClientsSimple: () => get('/api/crm/clients/simple', ClientsSimpleResponse),
  getClient: (id: string) => get(`/api/crm/clients/${id}`, ClientDetail),
  getClientTimeline: (id: string) =>
    get(`/api/crm/clients/${id}/timeline`, z.array(ClientTimelineEntry)),
  updateClient: (id: string, body: ClientUpdateRequest) =>
    patch(`/api/crm/clients/${id}`, body, EmptyResponse),
  archiveClient: (id: string) => post(`/api/crm/clients/${id}/archive`, {}, EmptyResponse),
  addContact: (clientId: string, body: ContactCreateRequest) =>
    post(`/api/crm/clients/${clientId}/contacts`, body, CreateClientIdResponse),
  mergeClients: (body: ClientMergeRequest) => post('/api/crm/clients/merge', body, EmptyResponse),

  // ----- Cost centers -----
  listCostCenters: () => get('/api/cost-centers', CostCentersResponse),
  createCostCenter: (body: CostCenterCreateRequest) =>
    post('/api/cost-centers', body, CreateIdResponse),
  renameCostCenter: (id: string, body: CostCenterRenameRequest) =>
    patch(`/api/cost-centers/${id}`, body, EmptyResponse),
  deactivateCostCenter: (id: string) =>
    post(`/api/cost-centers/${id}/deactivate`, {}, EmptyResponse),

  // ----- Projects -----
  listProjects: (
    params: {
      status?: z.infer<typeof ProjectStatus>;
      type?: z.infer<typeof ProjectType>;
      clientId?: string;
      search?: string;
    } = {},
  ) => {
    const qs = new URLSearchParams();
    if (params.status) qs.set('status', params.status);
    if (params.type) qs.set('type', params.type);
    if (params.clientId) qs.set('clientId', params.clientId);
    if (params.search) qs.set('search', params.search);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return get(`/api/projects${suffix}`, ProjectsResponse);
  },
  createProject: (body: ProjectCreateRequest) => post('/api/projects', body, ProjectCreateResponse),
  getProject: (id: string) => get(`/api/projects/${id}`, ProjectDetail),
  changeProjectStatus: (id: string, body: ProjectStatusChangeRequest) =>
    post(`/api/projects/${id}/status`, body, EmptyResponse),
  updateProject: (id: string, body: ProjectUpdateRequest) =>
    patch(`/api/projects/${id}`, body, EmptyResponse),
  toggleActualHours: (id: string) =>
    post(`/api/projects/${id}/actual-hours-toggle`, {}, ToggleActualHoursResponse),
  addProjectBudget: (id: string, body: BudgetRevisionCreateRequest) =>
    post(`/api/projects/${id}/budget`, body, CreateIdResponse),
  listRenewals: (
    params: {
      status?: z.infer<typeof RenewalStatusFilter>;
      kind?: z.infer<typeof RenewalKind>;
      projectId?: string;
      clientId?: string;
      search?: string;
      withinDays?: number;
    } = {},
  ) => {
    const qs = new URLSearchParams();
    if (params.status) qs.set('status', params.status);
    if (params.kind) qs.set('kind', params.kind);
    if (params.projectId) qs.set('projectId', params.projectId);
    if (params.clientId) qs.set('clientId', params.clientId);
    if (params.search) qs.set('search', params.search);
    if (params.withinDays !== undefined) qs.set('withinDays', String(params.withinDays));
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return get(`/api/renewals${suffix}`, RenewalListResponse);
  },
  createRenewal: (body: RenewalInput) => post('/api/renewals', body, CreateIdResponse),
  updateRenewal: (id: string, body: RenewalUpdateRequest) =>
    patch(`/api/renewals/${id}`, body, EmptyResponse),
  listDueRenewals: (withinDays = 60) =>
    get(`/api/renewals/due?withinDays=${withinDays}`, RenewalListResponse),

  // ----- Tasks -----
  listProjectTasks: (projectId: string) =>
    get(`/api/projects/${projectId}/tasks`, TaskTreeResponse),
  createTask: (projectId: string, body: TaskCreateRequest) =>
    post(`/api/projects/${projectId}/tasks`, body, CreateIdResponse),
  updateTask: (id: string, body: TaskUpdateRequest) =>
    patch(`/api/tasks/${id}`, body, EmptyResponse),
  reopenTask: (id: string, reason: string, toStatusId?: string) =>
    post(`/api/tasks/${id}/reopen`, { reason, toStatusId }, EmptyResponse),
  archiveTask: (id: string) => post(`/api/tasks/${id}/archive`, {}, EmptyResponse),
  listTaskStatuses: (projectId: string) =>
    get(`/api/projects/${projectId}/task-statuses`, TaskStatusesResponse),
  createTaskStatus: (projectId: string, body: TaskStatusCreateRequest) =>
    post(`/api/projects/${projectId}/task-statuses`, body, z.object({ id: z.string() })),
  updateTaskStatus: (id: string, body: TaskStatusUpdateRequest) =>
    patch(`/api/task-statuses/${id}`, body, EmptyResponse),
  reorderTaskStatuses: (projectId: string, orderedIds: string[]) =>
    post(`/api/projects/${projectId}/task-statuses/reorder`, { orderedIds }, EmptyResponse),
  getTaskHistory: (id: string) => get(`/api/tasks/${id}/history`, TaskHistoryResponse),
  listTaskComments: (id: string) => get(`/api/tasks/${id}/comments`, TaskCommentsResponse),
  addTaskComment: (id: string, body: TaskCommentCreateRequest) =>
    post(`/api/tasks/${id}/comments`, body, CreateIdResponse),
  getTaskTemplates: (projectType: z.infer<typeof ProjectType>) =>
    get(`/api/task-templates?projectType=${projectType}`, TaskTemplatesResponse),

  // ----- Allocations -----
  getAllocationMatrix: (month: string) =>
    get(`/api/allocations?month=${month}`, AllocationMatrixResponse),
  upsertAllocations: (body: AllocationUpsertRequest) =>
    post('/api/allocations', body, EmptyResponse),
  copyForwardAllocations: (body: AllocationCopyForwardRequest) =>
    post('/api/allocations/copy-forward', body, CopyForwardResponse),

  // ----- My tasks -----
  getMyTasks: () => get('/api/my-tasks', MyTasksInbox),

  // ----- Ledger: accounts -----
  listAccounts: () => get('/api/accounts', AccountsListResponse),
  createAccount: (body: AccountCreateRequest) => post('/api/accounts', body, CreateIdResponse),
  deactivateAccount: (id: string) => post(`/api/accounts/${id}/deactivate`, {}, EmptyResponse),

  // ----- Ledger: categories -----
  listCategories: (params: { kind?: z.infer<typeof CategoryKind> } = {}) => {
    const qs = new URLSearchParams();
    if (params.kind) qs.set('kind', params.kind);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return get(`/api/categories${suffix}`, CategoriesListResponse);
  },
  createCategory: (body: CategoryCreateRequest) => post('/api/categories', body, CreateIdResponse),
  deactivateCategory: (id: string) => post(`/api/categories/${id}/deactivate`, {}, EmptyResponse),

  // ----- Ledger: transactions -----
  listTransactions: (
    params: {
      accountId?: string;
      status?: z.infer<typeof TransactionStatus>;
      from?: string;
      to?: string;
      projectId?: string;
      costCenterId?: string;
      unmapped?: boolean;
      page?: number;
      pageSize?: number;
    } = {},
  ) => {
    const qs = new URLSearchParams();
    if (params.accountId) qs.set('accountId', params.accountId);
    if (params.status) qs.set('status', params.status);
    if (params.from) qs.set('from', params.from);
    if (params.to) qs.set('to', params.to);
    if (params.projectId) qs.set('projectId', params.projectId);
    if (params.costCenterId) qs.set('costCenterId', params.costCenterId);
    if (params.unmapped) qs.set('unmapped', 'true');
    if (params.page) qs.set('page', String(params.page));
    if (params.pageSize) qs.set('pageSize', String(params.pageSize));
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return get(`/api/transactions${suffix}`, TransactionListResponse);
  },
  getTransaction: (id: string) => get(`/api/transactions/${id}`, TransactionDetail),
  createTransaction: (body: TransactionCreateRequest) =>
    post('/api/transactions', body, CreateTransactionResponse),
  previewPastedRows: (body: PastedRowsPreviewRequest) =>
    post('/api/transactions/batch-preview', body, PastedRowsPreviewResponse),
  createTransactionBatch: (body: TransactionBatchCreateRequest) =>
    post('/api/transactions/batch', body, CreateBatchResponse),
  createTransfer: (body: TransferCreateRequest) =>
    post('/api/transactions/transfer', body, CreateTransferResponse),
  mapTransaction: (id: string, body: TransactionMapRequest) =>
    post(`/api/transactions/${id}/map`, body, EmptyResponse),
  reverseTransaction: (id: string, body: TransactionReverseRequest) =>
    post(`/api/transactions/${id}/reverse`, body, CreateReversalResponse),
  listUnmapped: (params: { accountId?: string; page?: number; pageSize?: number } = {}) => {
    const qs = new URLSearchParams();
    if (params.accountId) qs.set('accountId', params.accountId);
    if (params.page) qs.set('page', String(params.page));
    if (params.pageSize) qs.set('pageSize', String(params.pageSize));
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return get(`/api/unmapped${suffix}`, TransactionListResponse);
  },

  // ----- Bank templates -----
  listBankTemplates: () => get('/api/bank-templates', BankTemplatesListResponse),
  createBankTemplate: (body: BankTemplateCreateRequest) =>
    post('/api/bank-templates', body, CreateIdResponse),
  deactivateBankTemplate: (id: string) =>
    post(`/api/bank-templates/${id}/deactivate`, {}, EmptyResponse),

  // ----- Statements -----
  listStatements: (
    params: { accountId?: string; status?: z.infer<typeof StatementFileStatus> } = {},
  ) => {
    const qs = new URLSearchParams();
    if (params.accountId) qs.set('accountId', params.accountId);
    if (params.status) qs.set('status', params.status);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return get(`/api/statements${suffix}`, StatementsListResponse);
  },
  getStatement: (id: string) => get(`/api/statements/${id}`, StatementSummary),
  uploadStatement: (accountId: string, bankTemplateId: string, file: File) => {
    const form = new FormData();
    form.append('accountId', accountId);
    form.append('bankTemplateId', bankTemplateId);
    form.append('file', file, file.name);
    return postMultipart('/api/statements/upload', form, StatementUploadResult);
  },
  listStatementRows: (id: string, params: { page?: number; pageSize?: number } = {}) => {
    const qs = new URLSearchParams();
    if (params.page) qs.set('page', String(params.page));
    if (params.pageSize) qs.set('pageSize', String(params.pageSize));
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return get(`/api/statements/${id}/rows${suffix}`, StatementRowsResponse);
  },
  confirmStatementRow: (fileId: string, rowId: string, body: StatementRowConfirmRequest) =>
    post(`/api/statements/${fileId}/rows/${rowId}/confirm`, body, ConfirmRowResponse),
  skipStatementRow: (fileId: string, rowId: string, body: StatementRowSkipRequest) =>
    post(`/api/statements/${fileId}/rows/${rowId}/skip`, body, EmptyResponse),
  bulkConfirmStatementRows: (fileId: string, body: StatementBulkConfirmRequest) =>
    post(`/api/statements/${fileId}/rows/bulk-confirm`, body, BulkConfirmResponse),
  bulkSkipStatementRows: (fileId: string, body: StatementBulkSkipRequest) =>
    post(`/api/statements/${fileId}/rows/bulk-skip`, body, BulkSkipResponse),
  rerunSuggestions: (fileId: string) =>
    post(`/api/statements/${fileId}/suggest`, {}, RerunSuggestResponse),
  postStatement: (fileId: string, body: StatementPostRequest) =>
    post(`/api/statements/${fileId}/post`, body, StatementPostResult),

  // ----- Billing -----
  listDocuments: (
    params: {
      status?: z.infer<typeof DocumentStatus>;
      docType?: z.infer<typeof DocType>;
      clientId?: string;
      projectId?: string;
      from?: string;
      to?: string;
      page?: number;
      pageSize?: number;
    } = {},
  ) => {
    const qs = new URLSearchParams();
    if (params.status) qs.set('status', params.status);
    if (params.docType) qs.set('docType', params.docType);
    if (params.clientId) qs.set('clientId', params.clientId);
    if (params.projectId) qs.set('projectId', params.projectId);
    if (params.from) qs.set('from', params.from);
    if (params.to) qs.set('to', params.to);
    if (params.page) qs.set('page', String(params.page));
    if (params.pageSize) qs.set('pageSize', String(params.pageSize));
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return get(`/api/documents${suffix}`, DocumentListResponse);
  },
  getDocument: (id: string) => get(`/api/documents/${id}`, DocumentDetail),
  getNextDocumentNumber: (docType: z.infer<typeof DocType>, issueDate: string) =>
    get(
      `/api/documents/next-number?docType=${docType}&issueDate=${issueDate}`,
      NextDocumentNumberResponse,
    ),
  listDocumentNumberFormats: () =>
    get('/api/documents/number-formats', DocumentNumberFormatListResponse),
  addDocumentNumberFormat: (body: DocumentNumberFormatCreateRequest) =>
    post('/api/documents/number-formats', body, CreateIdResponse),
  createDocument: (body: DocumentCreateRequest) =>
    post('/api/documents', body, z.object({ documentId: z.string() })),
  updateDocument: (id: string, body: DocumentUpdateDraftRequest) =>
    patch(`/api/documents/${id}`, body, EmptyResponse),
  issueDocument: (id: string) =>
    post(
      `/api/documents/${id}/issue`,
      {},
      z.object({ documentId: z.string(), number: z.string() }),
    ),
  applyPayment: (id: string, body: PaymentApplicationRequest) =>
    post(
      `/api/documents/${id}/payments`,
      body,
      z.object({ paymentApplicationId: z.string(), newStatus: DocumentStatus }),
    ),
  writeOffDocument: (id: string, body: WriteOffRequest) =>
    post(`/api/documents/${id}/write-off`, body, EmptyResponse),
  createCreditNote: (body: CreditNoteCreateRequest) =>
    post('/api/credit-notes', body, z.object({ documentId: z.string() })),
  listMyPayslips: () => get('/api/me/payslips', z.array(PayslipListEntry)),
  getCac: (from: string, to: string) =>
    get(`/api/analytics/cac?from=${from}&to=${to}`, CacResponse),
  getLtv: (months = 36) => get(`/api/analytics/ltv?months=${months}`, LtvResponse),
  getProjectCash: (rateBpp = 1200) =>
    get(`/api/analytics/project-cash?rateBpp=${rateBpp}`, ProjectCashResponse),
  getTco: () => get('/api/analytics/tco', TcoResponse),
  downloadPayslip: (runId: string, itemId: string): Promise<Blob> =>
    fetchBlob(
      `/api/payroll-runs/${runId}/items/${itemId}/payslip.pdf`,
      { method: 'GET' },
      'application/pdf',
    ),
  downloadDocumentPdf: (id: string): Promise<Blob> =>
    fetchBlob(`/api/documents/${id}/pdf`, { method: 'GET' }, 'application/pdf'),
  getArAging: (asOf: string) =>
    get(`/api/ar-aging?asOf=${encodeURIComponent(asOf)}`, ArAgingResponse),
  downloadGstr1: (month: string): Promise<Blob> =>
    fetchBlob(`/api/gstr1?month=${encodeURIComponent(month)}`, { method: 'GET' }, 'text/csv'),

  // ----- M6: Period Close -----
  listPeriods: () => get('/api/period-close', z.array(PeriodSummary)),
  getPeriodChecklist: (month: string) =>
    get(`/api/period-close/${encodeURIComponent(month)}/checklist`, ChecklistResponse),
  softClosePeriod: (month: string) =>
    post(
      `/api/period-close/${encodeURIComponent(month)}/soft-close`,
      {},
      z.object({
        month: z.string(),
        status: z.string(),
      }),
    ),
  closePeriod: (month: string) =>
    post(
      `/api/period-close/${encodeURIComponent(month)}/close`,
      {},
      z.object({
        month: z.string(),
        status: z.string(),
        snapshotIds: z.array(z.string()),
      }),
    ),
  reopenPeriod: (month: string, body: ReopenRequest) =>
    post(
      `/api/period-close/${encodeURIComponent(month)}/reopen`,
      body,
      z.object({
        month: z.string(),
        status: z.string(),
        reopenCount: z.number().int(),
      }),
    ),
  listPeriodSnapshots: (month: string) =>
    get(`/api/period-close/${encodeURIComponent(month)}/snapshots`, z.array(SnapshotSummary)),

  // ----- M7: People / Attendance -----
  listLeaveTypes: () => get('/api/leave-types', z.array(LeaveTypeSummary)),
  createLeaveType: (body: LeaveTypeCreateRequest) =>
    post('/api/leave-types', body, CreateIdResponse),
  updateLeaveType: (id: string, body: LeaveTypeUpdateRequest) =>
    patch(`/api/leave-types/${id}`, body, EmptyResponse),

  listHolidays: (params: { fromMonth?: string; toMonth?: string } = {}) => {
    const qs = new URLSearchParams();
    if (params.fromMonth) qs.set('fromMonth', params.fromMonth);
    if (params.toMonth) qs.set('toMonth', params.toMonth);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return get(`/api/holidays${suffix}`, z.array(HolidaySummary));
  },
  createHoliday: (body: HolidayCreateRequest) => post('/api/holidays', body, CreateIdResponse),
  deactivateHoliday: (id: string) => post(`/api/holidays/${id}/deactivate`, {}, EmptyResponse),

  listLeaveRequests: (
    params: {
      personId?: string;
      status?: z.infer<typeof LeaveStatus>;
      fromMonth?: string;
      toMonth?: string;
    } = {},
  ) => {
    const qs = new URLSearchParams();
    if (params.personId) qs.set('personId', params.personId);
    if (params.status) qs.set('status', params.status);
    if (params.fromMonth) qs.set('fromMonth', params.fromMonth);
    if (params.toMonth) qs.set('toMonth', params.toMonth);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return get(`/api/leave-requests${suffix}`, z.array(LeaveRequestSummary));
  },
  getLeaveBalance: (personId: string, leaveTypeId: string, on: string) =>
    get(
      `/api/leave-balance?personId=${encodeURIComponent(personId)}&leaveTypeId=${encodeURIComponent(
        leaveTypeId,
      )}&on=${on}`,
      LeaveBalance,
    ),
  createLeaveRequest: (body: LeaveRequestCreateRequest) =>
    post('/api/leave-requests', body, CreateIdResponse),
  approveLeaveRequest: (id: string, body: LeaveRequestDecideRequest = {}) =>
    post(`/api/leave-requests/${id}/approve`, body, EmptyResponse),
  rejectLeaveRequest: (id: string, body: LeaveRequestDecideRequest = {}) =>
    post(`/api/leave-requests/${id}/reject`, body, EmptyResponse),
  availLeaveRequest: (id: string) => post(`/api/leave-requests/${id}/avail`, {}, EmptyResponse),
  cancelLeaveRequest: (id: string, body: LeaveRequestCancelRequest) =>
    post(`/api/leave-requests/${id}/cancel`, body, EmptyResponse),

  getAttendanceMonth: (month: string) =>
    get(`/api/attendance/${encodeURIComponent(month)}`, AttendanceMonthResponse),
  lockAttendanceMonth: (month: string) =>
    post(`/api/attendance/${encodeURIComponent(month)}/lock`, {}, AttendanceLockResponse),
  getAttendanceCalendar: (month: string) =>
    get(`/api/attendance/${encodeURIComponent(month)}/calendar`, AttendanceCalendarResponse),
  unlockAttendanceMonth: (month: string, body: AttendanceUnlockRequest) =>
    post(`/api/attendance/${encodeURIComponent(month)}/unlock`, body, EmptyResponse),

  // ----- M8: Payroll — statutory config -----
  listStatutoryConfig: () => get('/api/statutory-config', z.array(StatutoryConfigSummary)),
  createStatutoryConfig: (body: StatutoryConfigCreateRequest) =>
    post('/api/statutory-config', body, CreateIdResponse),
  verifyStatutoryConfig: (id: string, body: StatutoryConfigVerifyRequest) =>
    post(`/api/statutory-config/${id}/verify`, body, EmptyResponse),
  bootstrapStatutoryConfig: () =>
    post('/api/statutory-config/bootstrap', {}, z.object({ count: z.number().int() })),

  // ----- M8: Payroll — rates + structures + templates -----
  listPersonRates: (personId?: string) =>
    get(
      `/api/person-rates${personId ? `?personId=${encodeURIComponent(personId)}` : ''}`,
      z.array(PersonRateSummary),
    ),
  createPersonRate: (body: PersonRateCreateRequest) =>
    post('/api/person-rates', body, CreateIdResponse),
  listSalaryStructures: (personId?: string) =>
    get(
      `/api/salary-structures${personId ? `?personId=${encodeURIComponent(personId)}` : ''}`,
      z.array(SalaryStructureSummary),
    ),
  createSalaryStructure: (body: SalaryStructureCreateRequest) =>
    post('/api/salary-structures', body, CreateIdResponse),
  listComponentTemplates: () => get('/api/component-templates', z.array(ComponentTemplateSummary)),
  createComponentTemplate: (body: ComponentTemplateCreateRequest) =>
    post('/api/component-templates', body, CreateIdResponse),

  // ----- M8: Payroll — runs -----
  listPayrollRuns: () => get('/api/payroll-runs', z.array(PayrollRunSummary)),
  getPayrollRun: (id: string) => get(`/api/payroll-runs/${id}`, PayrollRunDetail),
  createPayrollRun: (body: PayrollRunCreateRequest) =>
    post('/api/payroll-runs', body, CreateIdResponse),
  previewPayrollRun: (id: string) =>
    post(`/api/payroll-runs/${id}/preview`, {}, z.object({ itemCount: z.number().int() })),
  lockPayrollRun: (id: string) => post(`/api/payroll-runs/${id}/lock`, {}, EmptyResponse),
  unlockPayrollRun: (id: string) => post(`/api/payroll-runs/${id}/unlock`, {}, EmptyResponse),
  postPayrollRun: (id: string) => post(`/api/payroll-runs/${id}/post`, {}, EmptyResponse),

  // ----- M9: Ideas & Initiatives -----
  listIdeas: (params: { status?: z.infer<typeof IdeaStatus> } = {}) => {
    const qs = new URLSearchParams();
    if (params.status) qs.set('status', params.status);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return get(`/api/ideas${suffix}`, z.array(IdeaSummary));
  },
  getIdea: (id: string) => get(`/api/ideas/${id}`, IdeaDetail),
  createIdea: (body: IdeaCreateRequest) => post('/api/ideas', body, CreateIdResponse),
  proposeIdea: (id: string, body: IdeaProposeRequest) =>
    post(`/api/ideas/${id}/propose`, body, EmptyResponse),
  pickUpIdea: (id: string, body: IdeaPickUpRequest) =>
    post(`/api/ideas/${id}/pick-up`, body, EmptyResponse),
  parkIdea: (id: string, body: IdeaParkRequest) =>
    post(`/api/ideas/${id}/park`, body, EmptyResponse),
  rejectIdea: (id: string, body: IdeaRejectRequest) =>
    post(`/api/ideas/${id}/reject`, body, EmptyResponse),
  resurfaceIdea: (id: string, body: IdeaResurfaceRequest) =>
    post(`/api/ideas/${id}/resurface`, body, EmptyResponse),
  approveIdea: (id: string, body: IdeaApproveRequest) =>
    post(`/api/ideas/${id}/approve`, body, IdeaApproveResult),
  voteIdea: (id: string) => post(`/api/ideas/${id}/vote`, {}, VoteToggleResult),
  listIdeaComments: (id: string) => get(`/api/ideas/${id}/comments`, z.array(CommentSummary)),
  addIdeaComment: (id: string, body: CommentCreateRequest) =>
    post(
      `/api/ideas/${id}/comments`,
      body,
      z.object({ id: z.string(), mentionedPersonIds: z.array(z.string()) }),
    ),
  listInitiativesDueForReview: () =>
    get(
      '/api/initiatives/reviews-due',
      z.array(
        z.object({
          projectId: z.string(),
          code: z.string(),
          name: z.string(),
          reviewDueOn: z.string(),
          overdueDays: z.number().int(),
        }),
      ),
    ),

  // ----- M10: Notifications & Queues polish -----
  getNotificationPreferences: () =>
    get('/api/me/notification-preferences', NotificationPreferences),
  updateNotificationPreferences: (body: NotificationPreferencesUpdateRequest) =>
    request(
      '/api/me/notification-preferences',
      { method: 'PUT', body: JSON.stringify(body) },
      NotificationPreferences,
    ),
  listQueueItems: () => get('/api/queue-items', QueueItemsResponse),

  // ----- M12: Analytics -----
  getMetricsDictionary: () => get('/api/analytics/metrics-dictionary', z.array(MetricEntry)),
  getDataHealth: () => get('/api/analytics/data-health', DataHealthResponse),
  getProjectPnl: (asOf?: string) =>
    get(`/api/analytics/project-pnl${asOf ? `?asOf=${asOf}` : ''}`, ProjectPnlResponse),
  getDso: () => get('/api/analytics/dso', DsoResponse),
  getReconciliationLag: () => get('/api/analytics/reconciliation-lag', ReconciliationLagResponse),
  getPayrollCostRatio: (month: string) =>
    get(`/api/analytics/payroll-cost-ratio?month=${month}`, PayrollCostRatioResponse),
  getPeopleKpi: (month: string) =>
    get(`/api/analytics/people-kpi?month=${month}`, PeopleKpiResponse),
  getUtilization: (month: string) =>
    get(`/api/analytics/utilization?month=${month}`, UtilizationResponse),
  getBudgetVariance: (asOf?: string) =>
    get(`/api/analytics/budget-variance${asOf ? `?asOf=${asOf}` : ''}`, BudgetVarianceResponse),

  // ----- Sales: service catalog -----
  listServiceOfferings: () => get('/api/sales/service-offerings', z.array(ServiceOfferingSummary)),
  upsertServiceOffering: (body: ServiceOfferingUpsertRequest) =>
    post('/api/sales/service-offerings', body, ServiceOfferingUpsertResponse),
  deactivateServiceOffering: (id: string) =>
    post(`/api/sales/service-offerings/${id}/deactivate`, {}, EmptyResponse),

  // ----- Sales: prospects -----
  listProspects: (params: Partial<z.input<typeof ProspectListFilters>> = {}) => {
    const qs = new URLSearchParams();
    if (params.ownerId) qs.set('ownerId', params.ownerId);
    if (params.stage) qs.set('stage', params.stage);
    if (params.status) qs.set('status', params.status);
    if (params.leadChannelId) qs.set('leadChannelId', params.leadChannelId);
    if (params.serviceOfferingId) qs.set('serviceOfferingId', params.serviceOfferingId);
    if (params.staleOnly !== undefined) qs.set('staleOnly', String(params.staleOnly));
    if (params.search) qs.set('search', String(params.search));
    if (params.page !== undefined) qs.set('page', String(params.page));
    if (params.pageSize !== undefined) qs.set('pageSize', String(params.pageSize));
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return get(`/api/sales/prospects${suffix}`, ProspectListResponse);
  },
  getProspect: (id: string) => get(`/api/sales/prospects/${id}`, ProspectDetail),
  createProspect: (body: ProspectCreateRequest) =>
    post('/api/sales/prospects', body, CreateIdResponse),
  updateProspect: (id: string, body: ProspectUpdateRequest) =>
    patch(`/api/sales/prospects/${id}`, body, EmptyResponse),
  transitionProspect: (id: string, body: ProspectStageTransitionRequest) =>
    post(`/api/sales/prospects/${id}/transition`, body, ProspectTransitionResponse),
  correctProspectStage: (id: string, body: ProspectStageCorrectionRequest) =>
    post(`/api/sales/prospects/${id}/correct-stage`, body, EmptyResponse),
  listProspectActivities: (id: string) =>
    get(`/api/sales/prospects/${id}/activities`, z.array(SalesActivitySummary)),
  addProspectActivity: (id: string, body: SalesActivityCreateRequest) =>
    post(`/api/sales/prospects/${id}/activities`, body, CreateIdResponse),

  // ----- Sales: dashboard -----
  getSalesDashboard: (window: z.infer<typeof SalesDashboardWindow>) =>
    get(`/api/sales/dashboard?window=${window}`, SalesDashboardResponse),

  // ----- Sales: C-suite members (Admin) -----
  listCsuiteMembers: () => get('/api/sales/csuite-members', z.array(CsuiteMemberSummary)),
  upsertCsuiteMember: (body: CsuiteMemberUpsertRequest) =>
    post('/api/sales/csuite-members', body, CsuiteMemberUpsertResponse),

  // ----- API tokens (MCP / integrations) -----
  listApiTokens: () => get('/api/tokens', z.array(ApiTokenSummary)),
  createApiToken: (body: ApiTokenCreateRequest) =>
    post('/api/tokens', body, ApiTokenCreateResponse),
  revokeApiToken: (id: string) => post(`/api/tokens/${id}/revoke`, {}, EmptyResponse),

  // ----- Work log (M13, spec 7.3.1) -----
  getAbsencesSummary: () => get('/api/attendance-days/absences/summary', AbsencesSummary),
  listOpenAbsences: (personId: string) =>
    get(
      `/api/attendance-days/absences?personId=${encodeURIComponent(personId)}`,
      z.array(AttendanceDayRow),
    ),
  ruleOnAbsences: (body: AbsenceBulkRulingRequest) =>
    post('/api/attendance-days/absences/rule', body, AbsenceBulkRulingResult),
  listBackfillableDays: () => get('/api/work-log/backfillable', z.array(AttendanceDayRow)),
  markAbsenceLop: (personId: string, date: string, note?: string) =>
    post(`/api/attendance-days/${personId}/${date}/lop`, { note }, EmptyResponse),
  excuseAbsence: (personId: string, date: string, note?: string) =>
    post(`/api/attendance-days/${personId}/${date}/excuse`, { note }, EmptyResponse),
  allowAbsenceBackfill: (personId: string, date: string, note?: string) =>
    post(`/api/attendance-days/${personId}/${date}/allow-backfill`, { note }, EmptyResponse),
  backfillWorkSession: (date: string, body: BackfillSessionRequest) =>
    post(`/api/work-log/backfill/${date}`, body, WorkSessionDetail),
  getCurrentWorkSession: () => get('/api/work-log/current', CurrentWorkSessionResponse),
  clockIn: (body: ClockInRequest) => post('/api/work-log/clock-in', body, WorkSessionDetail),
  startWorkBreak: () => post('/api/work-log/breaks/start', {}, WorkSessionDetail),
  endWorkBreak: () => post('/api/work-log/breaks/end', {}, WorkSessionDetail),
  clockOut: (body: ClockOutRequest) => post('/api/work-log/clock-out', body, WorkSessionDetail),
  listWorkSessions: (params: { from?: string; to?: string; personId?: string } = {}) => {
    const q = new URLSearchParams();
    if (params.from) q.set('from', params.from);
    if (params.to) q.set('to', params.to);
    if (params.personId) q.set('personId', params.personId);
    const qs = q.toString();
    return get(`/api/work-log/sessions${qs ? `?${qs}` : ''}`, z.array(WorkSessionDetail));
  },
  adjustWorkSession: (id: string, body: WorkSessionAdjustRequest) =>
    post(`/api/work-log/sessions/${id}/adjust`, body, WorkSessionDetail),
  getAllocationProposal: (month: string) =>
    get(`/api/work-log/allocation-proposal/${month}`, AllocationProposalResponse),
  confirmAllocationProposal: (month: string) =>
    post(
      `/api/work-log/allocation-proposal/${month}/confirm`,
      {},
      AllocationProposalConfirmResponse,
    ),
};
